import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2,
  Mail,
  Phone,
  Globe,
  MapPin,
  Clock,
  Languages,
  FileText,
  Save,
  Upload,
  Loader2,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import PasswordManagementSection from '../../settings/PasswordManagementSection';
import AgencyOwnerLoginSection from './AgencyOwnerLoginSection';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona (No DST)' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'UTC', label: 'UTC' },
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
];

/**
 * Agency Profile Section
 * 
 * Core business profile settings for the agency.
 */
const AgencyProfileSection = ({ agency, agencyId, isReadOnly, onUpdate }) => {
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    console.log('[AgencyProfileSection] PasswordManagementSection render reached');
  }, []);
  
  // Form state
  const [formData, setFormData] = useState({
    name: agency?.name || '',
    legal_name: agency?.legal_name || '',
    contact_email: agency?.contact_email || '',
    contact_phone: agency?.contact_phone || '',
    website: agency?.website || '',
    address: agency?.address || '',
    city: agency?.city || '',
    state: agency?.state || '',
    country: agency?.country || 'US',
    postal_code: agency?.postal_code || '',
    timezone: agency?.timezone || 'America/New_York',
    language: agency?.language || 'en',
    notes: agency?.notes || '',
    relationship_number: agency?.relationship_number || '',
    location_id: agency?.location_id || '',
  });
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSave = async () => {
    if (isReadOnly) return;
    
    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/agencies/${agencyId}/profile`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Agency profile updated successfully');
      if (onUpdate) onUpdate(formData);
    } catch (err) {
      console.error('Failed to update profile:', err);
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div
      className="space-y-6"
      data-testid="agency-profile-section"
      data-component="AgencyProfileSection"
    >
      {/* Section Header */}
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
            <Building2 className="text-amber-600" size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Agency Profile</h2>
            <p className="text-sm text-slate-500">
              Core business information and contact details
            </p>
          </div>
        </div>
      </div>
      
      {/* Form Content */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
        {/* Business Identity */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            Business Identity
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Friendly Agency Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="My Credit Repair Agency"
                disabled={isReadOnly}
                className="bg-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="legal_name">Legal Business Name</Label>
              <Input
                id="legal_name"
                value={formData.legal_name}
                onChange={(e) => handleChange('legal_name', e.target.value)}
                placeholder="My Credit Repair Agency LLC"
                disabled={isReadOnly}
                className="bg-white"
              />
            </div>
          </div>
          
          {/* Agency Logo */}
          <div className="space-y-2">
            <Label>Agency Logo</Label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center">
                {agency?.logo_url ? (
                  <img src={agency.logo_url} alt="Logo" className="w-full h-full object-contain rounded-xl" />
                ) : (
                  <Building2 className="text-slate-400" size={32} />
                )}
              </div>
              <div>
                <Button variant="outline" size="sm" disabled={isReadOnly}>
                  <Upload size={14} className="mr-2" />
                  Upload Logo
                </Button>
                <p className="text-xs text-slate-500 mt-1">
                  PNG, JPG up to 2MB. Recommended 200x200px
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <hr className="border-slate-200" />
        
        {/* Contact Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            Contact Information
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact_email">
                <Mail size={14} className="inline mr-1" />
                Agency Contact Email
              </Label>
              <Input
                id="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => handleChange('contact_email', e.target.value)}
                placeholder="contact@agency.com"
                disabled={isReadOnly}
                className="bg-white"
              />
              <p className="text-xs text-slate-500" data-testid="contact-email-helper">
                Used for agency communications. This does not change a user&apos;s sign-in email.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contact_phone">
                <Phone size={14} className="inline mr-1" />
                Contact Phone
              </Label>
              <Input
                id="contact_phone"
                value={formData.contact_phone}
                onChange={(e) => handleChange('contact_phone', e.target.value)}
                placeholder="(555) 123-4567"
                disabled={isReadOnly}
                className="bg-white"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="website">
              <Globe size={14} className="inline mr-1" />
              Business Website
            </Label>
            <Input
              id="website"
              value={formData.website}
              onChange={(e) => handleChange('website', e.target.value)}
              placeholder="https://www.myagency.com"
              disabled={isReadOnly}
              className="bg-white"
            />
          </div>
        </div>
        
        <hr className="border-slate-200" />

        {/* Agency Owner Login — auth account, separate from contact email */}
        <AgencyOwnerLoginSection agencyId={agencyId} token={token} isReadOnly={isReadOnly} />

        <hr className="border-slate-200" />
        
        {/* Physical Address */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            <MapPin size={14} className="inline mr-1" />
            Physical Address
          </h3>
          
          <div className="space-y-2">
            <Label htmlFor="address">Street Address</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="123 Main Street, Suite 100"
              disabled={isReadOnly}
              className="bg-white"
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                placeholder="New York"
                disabled={isReadOnly}
                className="bg-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="state">State / Region</Label>
              <Input
                id="state"
                value={formData.state}
                onChange={(e) => handleChange('state', e.target.value)}
                placeholder="NY"
                disabled={isReadOnly}
                className="bg-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="postal_code">Postal Code</Label>
              <Input
                id="postal_code"
                value={formData.postal_code}
                onChange={(e) => handleChange('postal_code', e.target.value)}
                placeholder="10001"
                disabled={isReadOnly}
                className="bg-white"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="country">Country</Label>
            <Select
              value={formData.country}
              onValueChange={(value) => handleChange('country', value)}
              disabled={isReadOnly}
            >
              <SelectTrigger className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="US">United States</SelectItem>
                <SelectItem value="CA">Canada</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <hr className="border-slate-200" />
        
        {/* Regional Settings */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            Regional Settings
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="timezone">
                <Clock size={14} className="inline mr-1" />
                Time Zone
              </Label>
              <Select
                value={formData.timezone}
                onValueChange={(value) => handleChange('timezone', value)}
                disabled={isReadOnly}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {TIMEZONES.map(tz => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="language">
                <Languages size={14} className="inline mr-1" />
                Language
              </Label>
              <Select
                value={formData.language}
                onValueChange={(value) => handleChange('language', value)}
                disabled={isReadOnly}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {LANGUAGES.map(lang => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        
        <hr className="border-slate-200" />

        {/* Password Management — highlighted block below Regional Settings */}
        <div
          id="password-management"
          className="scroll-mt-24 rounded-xl border border-amber-200 bg-amber-50/40 p-6"
          data-testid="password-management-block"
        >
          <div
            data-testid="password-management-anchor"
            className="mb-4 rounded-lg border border-amber-300 bg-amber-100/80 px-3 py-2 text-sm font-medium text-amber-900"
          >
            Password management anchor
          </div>
          <PasswordManagementSection />
        </div>

        <hr className="border-slate-200" />
        
        {/* Internal References */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            Internal References
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="relationship_number">Relationship Number</Label>
              <Input
                id="relationship_number"
                value={formData.relationship_number}
                onChange={(e) => handleChange('relationship_number', e.target.value)}
                placeholder="REL-12345"
                disabled={isReadOnly}
                className="bg-white"
              />
              <p className="text-xs text-slate-500">Internal reference number</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="location_id">Location ID</Label>
              <Input
                id="location_id"
                value={formData.location_id}
                onChange={(e) => handleChange('location_id', e.target.value)}
                placeholder="LOC-001"
                disabled={isReadOnly}
                className="bg-white"
              />
              <p className="text-xs text-slate-500">For multi-location agencies</p>
            </div>
          </div>
        </div>
        
        <hr className="border-slate-200" />
        
        {/* Notes */}
        <div className="space-y-2">
          <Label htmlFor="notes">
            <FileText size={14} className="inline mr-1" />
            Notes
          </Label>
          <Textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Internal notes about this agency..."
            rows={4}
            disabled={isReadOnly}
            className="bg-white"
          />
        </div>
      </div>
      
      {/* Save Button */}
      {!isReadOnly && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AgencyProfileSection;
